/**
 * Family Health Updates Page
 *
 * Shows health reports and visit summaries
 */

import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Card } from '@/core/components';
import { useAcknowledgeFamilyItem, useFamilyAcknowledgements, useFamilyDashboard } from '../hooks';
import { useAuth } from '@/core/hooks';
import { ActivityFeed, VisitCard } from '../components';
import { Heart, FileText, Activity, CheckCircle, ShieldCheck } from 'lucide-react';
import { getLocalStorage, setLocalStorage } from '@/utils/localStorage';
import type { UUID } from '@fuchsiahomecarecatalin/core/browser';

const FAMILY_ACK_KEY = 'family:acknowledgements';

const permittedDocuments = [
  {
    id: 'care-plan-summary',
    title: 'Care plan summary',
    description: 'Goals, routines, progress and family-safe support information.',
  },
  {
    id: 'visit-summary',
    title: 'Visit summaries',
    description: 'Completed visit status, visible notes and follow-up reminders.',
  },
  {
    id: 'health-update-policy',
    title: 'Health update policy',
    description: 'What information can be shared with family members through the portal.',
  },
];

function getAcknowledgements(): Record<string, string> {
  return getLocalStorage<Record<string, string>>(FAMILY_ACK_KEY, {});
}

export const HealthUpdatesPage: React.FC = () => {
  const { user } = useAuth();
  const familyMemberId = user?.id as UUID | null;
  const [acknowledgements, setAcknowledgements] = useState<Record<string, string>>(() => getAcknowledgements());
  const { data: dashboardData, isLoading } = useFamilyDashboard(familyMemberId);
  const { data: serverAcknowledgements } = useFamilyAcknowledgements(familyMemberId);
  const acknowledgeItem = useAcknowledgeFamilyItem(familyMemberId);

  const clientName = dashboardData?.client?.name || 'your loved one';
  const visits = dashboardData?.upcomingVisits || [];
  const recentActivity = dashboardData?.recentActivity || [];

  // Filter for completed visits
  const completedVisits = visits.filter(v => v.status === 'COMPLETED');

  const acknowledge = (id: string, label: string) => {
    const next = { ...getAcknowledgements(), [id]: new Date().toISOString() };
    setLocalStorage(FAMILY_ACK_KEY, next);
    setAcknowledgements(next);
    if (familyMemberId) {
      acknowledgeItem.mutate({ itemId: id, label, itemType: id.startsWith('document-') ? 'document' : 'health-update' });
    } else {
      toast.success(`${label} confirmed as received`);
    }
  };

  const isAcknowledged = (id: string) =>
    Boolean(acknowledgements[id] || serverAcknowledgements?.some((item) => item.itemId === id));

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-16 bg-gray-200 rounded-lg" />
        <div className="h-96 bg-gray-200 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Health Updates</h1>
        <p className="text-gray-600 mt-1">
          Visit summaries and health reports for {clientName}
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card padding="md">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{completedVisits.length}</div>
              <div className="text-sm text-gray-600">Recent Visits</div>
            </div>
          </div>
        </Card>

        <Card padding="md">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <Heart className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{recentActivity.length}</div>
              <div className="text-sm text-gray-600">Activity Updates</div>
            </div>
          </div>
        </Card>

        <Card padding="md">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-purple-100">
              <Activity className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {dashboardData?.activeCarePlan ? 'Active' : 'None'}
              </div>
              <div className="text-sm text-gray-600">Care Plan Status</div>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_1fr]">
        <Card padding="lg" className="border-2 border-emerald-100 bg-emerald-50">
          <div className="flex items-start gap-3">
            <CheckCircle className="mt-1 h-6 w-6 text-emerald-700" />
            <div>
              <p className="text-sm font-bold uppercase tracking-wide text-emerald-700">Automatic visit notification</p>
              <h2 className="mt-1 text-xl font-bold text-emerald-950">Visit completed updates</h2>
              <p className="mt-2 text-sm leading-6 text-emerald-900">
                When a visit is marked complete by the care team, the family receives a clear update here with family-safe details only.
              </p>
              <button
                type="button"
                onClick={() => acknowledge('latest-health-update', 'Latest health update')}
                className="mt-4 inline-flex items-center gap-2 rounded-md bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800"
              >
                <CheckCircle className="h-4 w-4" />
                {isAcknowledged('latest-health-update') ? 'Received' : 'Confirm received'}
              </button>
            </div>
          </div>
        </Card>

        <Card padding="lg">
          <div className="flex items-start gap-3">
            <ShieldCheck className="mt-1 h-6 w-6 text-[#6c3a80]" />
            <div>
              <p className="text-sm font-bold uppercase tracking-wide text-[#6c3a80]">Family-safe filter</p>
              <h2 className="mt-1 text-xl font-bold text-gray-950">Only permitted information is shown</h2>
              <p className="mt-2 text-sm leading-6 text-gray-600">
                Health updates show visit outcomes, family-safe notes, agreed care plan progress and permitted documents. Internal staff notes, payroll details, private admin records and restricted safeguarding information are hidden from this portal.
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Visit Summaries */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Recent Visit Summaries
        </h2>
        {completedVisits.length > 0 ? (
          <div className="space-y-4">
            {completedVisits.slice(0, 5).map((visit) => (
              <VisitCard key={visit.id} visit={visit} />
            ))}
          </div>
        ) : (
          <Card padding="lg">
            <div className="text-center py-8">
              <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500">No completed visits yet</p>
              <p className="text-sm text-gray-400 mt-1">
                Visit summaries will appear here after visits are completed
              </p>
            </div>
          </Card>
        )}
      </div>

      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Visit History Visible To Family
        </h2>
        <Card padding="md">
          {visits.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {visits.slice(0, 10).map((visit) => (
                <div key={visit.id} className="flex flex-col gap-2 py-4 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="font-semibold text-gray-950">{visit.clientName || clientName}</p>
                    <p className="text-sm text-gray-600">
                      {visit.scheduledStartTimeFormatted || visit.scheduledStartTime || 'Scheduled visit'} with {visit.caregiverName || 'care team'}
                    </p>
                  </div>
                  <span className="inline-flex w-fit rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-gray-700">
                    {visit.status || 'Scheduled'}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="py-6 text-center text-sm text-gray-500">No visit history is available yet.</p>
          )}
        </Card>
      </div>

      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Documents Permitted For Family
        </h2>
        <div className="grid gap-4 md:grid-cols-3">
          {permittedDocuments.map((document) => (
            <Card key={document.id} padding="md">
              <div className="flex h-full flex-col justify-between gap-4">
                <div>
                  <FileText className="h-6 w-6 text-[#6c3a80]" />
                  <h3 className="mt-3 font-semibold text-gray-950">{document.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-gray-600">{document.description}</p>
                </div>
                <button
                  type="button"
                  onClick={() => acknowledge(`document-${document.id}`, document.title)}
                  className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  <CheckCircle className="h-4 w-4" />
                  {isAcknowledged(`document-${document.id}`) ? 'Received' : 'Confirm received'}
                </button>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Activity Feed */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Activity className="h-5 w-5" />
          Recent Activity
        </h2>
        <Card padding="md">
          {recentActivity.length > 0 ? (
            <ActivityFeed activities={recentActivity} showLimit={10} />
          ) : (
            <div className="text-center py-8">
              <Activity className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500">No recent activity</p>
            </div>
          )}
        </Card>
      </div>

      {/* Information Box */}
      <Card padding="md" className="bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <Heart className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-blue-900 mb-1">About Health Updates</h3>
            <p className="text-sm text-blue-800">
              This page shows visit summaries, health reports, and activity updates for {clientName}.
              You'll receive notifications when new updates are available. If you have questions about
              any health information, please contact the care team directly.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};
